/**
 * Content Manager - AI Service
 *
 * AI-powered content assistance using Claude API.
 */

import type { AIRequest, AIResponse, AIFeatureType, AIUsageStats, ApiResponse } from '../types';
import { logger } from '../lib/logger';
import { useConfigStore } from '../stores/configStore';

// =============================================================================
// CLAUDE API CONFIGURATION
// =============================================================================

const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';
const CLAUDE_API_VERSION = '2023-06-01';
const DEFAULT_MODEL = 'claude-sonnet-4-20250514';

/**
 * Get Claude API key from config store or environment (with localStorage migration)
 */
function getClaudeApiKey(): string | null {
  // Check config store first (database-backed)
  const storeKey = useConfigStore.getState().config.credentials?.claudeApiKey;
  if (storeKey) return storeKey;

  // Fallback: Check localStorage for migration (legacy storage)
  if (typeof window !== 'undefined') {
    const localKey = localStorage.getItem('claude_api_key');
    if (localKey) {
      // Migrate to config store (fire and forget)
      useConfigStore.getState().setClaudeApiKey(localKey).then(() => {
        localStorage.removeItem('claude_api_key');
      });
      return localKey;
    }
  }

  // Environment variable (for build-time configuration)
  const envKey = process.env.REACT_APP_CLAUDE_API_KEY;
  if (envKey) return envKey;

  return null;
}

/**
 * Check if Claude API is configured
 */
export function isClaudeConfigured(): boolean {
  return !!getClaudeApiKey();
}

/**
 * Set Claude API key (saves to database via config store)
 */
export async function setClaudeApiKey(key: string): Promise<void> {
  await useConfigStore.getState().setClaudeApiKey(key);
}

/**
 * Clear Claude API key
 */
export async function clearClaudeApiKey(): Promise<void> {
  await useConfigStore.getState().setClaudeApiKey(null);
}

// =============================================================================
// AI PROMPTS
// =============================================================================

const AI_PROMPTS: Record<AIFeatureType, string> = {
  generate_ideas: `You are a creative content strategist. Generate 5 unique, engaging content ideas based on the following topic or theme. Each idea should include:
- A compelling title
- A brief description (1-2 sentences)
- Suggested content format (blog, video, social post, etc.)

Topic: {input}

{context}

Format your response as a numbered list.`,

  improve_content: `You are an expert content editor. Improve the following content while maintaining the original message and tone. Focus on:
- Clarity and readability
- Engagement and flow
- Grammar and style
- Stronger opening and closing

Original content:
{input}

{context}

Provide the improved version, followed by a brief explanation of the key changes.`,

  generate_headlines: `You are a headline writing expert. Generate 10 compelling headlines for the following content or topic. Vary the styles:
- Include emotional triggers
- Use numbers where appropriate
- Ask questions
- Make bold statements
- Create curiosity gaps

Topic/Content: {input}

{context}

Format as a numbered list, with the strongest headlines first.`,

  generate_seo: `You are an SEO specialist. Generate optimized SEO elements for the following content:

Content: {input}

{context}

Provide:
1. SEO Title (50-60 characters)
2. Meta Description (150-160 characters)
3. Focus Keyword
4. 5 Related Keywords
5. URL Slug Suggestion

Format each element clearly with labels.`,

  generate_social: `You are a social media expert. Create platform-optimized social media posts for the following content:

Content: {input}

{context}

Generate posts for:
1. LinkedIn (professional, 1300 char max)
2. Twitter/X (engaging, 280 char max)
3. Facebook (conversational, with CTA)
4. Instagram (visual-focused caption with hashtags)

Include relevant emojis and hashtags where appropriate.`,

  summarize: `You are an expert at distilling complex information. Summarize the following content in:
1. One sentence (TL;DR)
2. One paragraph (Executive Summary)
3. Three bullet points (Key Takeaways)

Content: {input}

{context}

Format each summary type clearly.`,

  expand: `You are a content writer. Expand the following brief content into a comprehensive piece. Add:
- More detail and examples
- Relevant context
- Supporting points
- Smooth transitions

Brief content: {input}

{context}

Expand this to approximately 3-4x the original length while maintaining the core message.`,
};

// =============================================================================
// MOCK RESPONSES (fallback when API key not configured)
// =============================================================================

const generateMockResponse = (type: AIFeatureType, input: string): string[] => {
  const mockResponses: Record<AIFeatureType, () => string[]> = {
    generate_ideas: () => [
      `1. "The Ultimate Guide to ${input}" - A comprehensive deep-dive exploring every aspect of the topic. Format: Long-form blog post or whitepaper.`,
      `2. "${input}: 10 Things You're Doing Wrong" - An engaging listicle that challenges common misconceptions. Format: Blog post with infographic.`,
      `3. "Expert Roundup: Leaders Share Their ${input} Strategies" - Interview-style content featuring industry experts. Format: Video series or podcast.`,
      `4. "The Future of ${input}: 2025 Predictions" - Forward-looking analysis of trends and developments. Format: Report or thought leadership piece.`,
      `5. "${input} Case Study: How [Company] Achieved [Result]" - Real-world success story with actionable insights. Format: Case study or video testimonial.`,
    ],
    improve_content: () => [
      `Here's the improved version of your content:\n\n${input}\n\n[Enhanced with stronger verbs, clearer structure, and more engaging language. Key changes: Strengthened the opening hook, added transitional phrases, and tightened the conclusion with a clear call-to-action.]`,
    ],
    generate_headlines: () => [
      `1. "Why ${input} Is the Secret Weapon Top Performers Use"`,
      `2. "7 ${input} Mistakes That Are Costing You Money"`,
      `3. "The ${input} Framework That Changed Everything"`,
      `4. "Stop Doing ${input} Wrong: The Complete Guide"`,
      `5. "How to Master ${input} in Just 30 Days"`,
      `6. "What Nobody Tells You About ${input}"`,
      `7. "${input}: The Ultimate Beginner's Guide for 2025"`,
      `8. "Why Your ${input} Strategy Isn't Working (And How to Fix It)"`,
      `9. "The Science Behind Successful ${input}"`,
      `10. "${input} Secrets From Industry Leaders"`,
    ],
    generate_seo: () => [
      `**SEO Title:** Master ${input}: Expert Strategies for Success | [Brand]`,
      `**Meta Description:** Discover proven ${input} strategies that drive results. Learn expert tips, best practices, and actionable insights to transform your approach today.`,
      `**Focus Keyword:** ${input.toLowerCase().replace(/\s+/g, '-')}`,
      `**Related Keywords:** ${input} strategies, ${input} tips, how to ${input}, ${input} best practices, ${input} guide`,
      `**URL Slug:** ${input.toLowerCase().replace(/\s+/g, '-')}-complete-guide`,
    ],
    generate_social: () => [
      `**LinkedIn:**\n🎯 Just published: A deep dive into ${input}\n\nAfter months of research, I've compiled the most effective strategies that actually work.\n\nHere's what you'll learn:\n✅ Key insight 1\n✅ Key insight 2\n✅ Key insight 3\n\nThe results speak for themselves.\n\nLink in comments 👇\n\n#${input.replace(/\s+/g, '')} #Marketing #Strategy`,
      `**Twitter/X:**\n🧵 Everything I've learned about ${input} (after 1000+ hours):\n\nThe key insight most people miss:\n\n→ Point 1\n→ Point 2\n→ Point 3\n\nSave this. You'll need it.`,
      `**Facebook:**\nWe just dropped something big! 🎉\n\nOur complete guide to ${input} is now live. Whether you're just starting out or looking to level up, this one's for you.\n\nCheck it out and let us know what you think! 👇`,
      `**Instagram:**\n${input} doesn't have to be complicated 💡\n\nSwipe through for 5 game-changing tips that will transform your approach ➡️\n\nSave this post for later! 🔖\n\n#${input.replace(/\s+/g, '')} #ContentTips #MarketingStrategy #GrowthMindset #BusinessTips`,
    ],
    summarize: () => [
      `**TL;DR:** ${input.substring(0, 100)}...`,
      `**Executive Summary:** This content explores the key aspects of the topic, providing actionable insights and practical strategies for implementation. The main focus is on delivering value through clear, structured information.`,
      `**Key Takeaways:**\n• First major point from the content\n• Second important insight to remember\n• Third actionable takeaway for implementation`,
    ],
    expand: () => [
      `Here is the expanded content:\n\n${input}\n\n[Expanded with additional context, examples, and supporting details. The content now includes a stronger introduction, detailed explanations of each point, real-world examples, and a comprehensive conclusion with next steps.]`,
    ],
  };

  return mockResponses[type]();
};

// =============================================================================
// CLAUDE API INTEGRATION
// =============================================================================

interface ClaudeResponse {
  id: string;
  type: string;
  role: string;
  content: Array<{
    type: string;
    text: string;
  }>;
  model: string;
  stop_reason: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

/**
 * Call Claude API with a prompt
 */
async function callClaudeAPI(
  prompt: string,
  model: string = DEFAULT_MODEL
): Promise<{ text: string; usage: { input_tokens: number; output_tokens: number } }> {
  const apiKey = getClaudeApiKey();

  if (!apiKey) {
    throw new Error('Claude API key not configured. Please set your API key in Settings.');
  }

  const response = await fetch(CLAUDE_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': CLAUDE_API_VERSION,
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 2048,
      messages: [
        {
          role: 'user',
          content: prompt,
        },
      ],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = (errorData as any).error?.message || response.statusText;
    throw new Error(`Claude API error: ${errorMessage}`);
  }

  const data: ClaudeResponse = await response.json();

  return {
    text: data.content[0]?.text || '',
    usage: data.usage,
  };
}

/**
 * Parse Claude response into suggestions array
 */
function parseClaudeResponse(text: string, type: AIFeatureType): string[] {
  // For types that return multiple items, split by numbered list or sections
  if (['generate_ideas', 'generate_headlines'].includes(type)) {
    // Split by numbered items (1., 2., etc.)
    const items = text.split(/\n(?=\d+\.)/).filter(item => item.trim());
    return items.length > 0 ? items : [text];
  }

  if (type === 'generate_social') {
    // Split by platform headers (**LinkedIn:**, **Twitter/X:**, etc.)
    const items = text.split(/\n(?=\*\*[A-Za-z/]+:\*\*)/).filter(item => item.trim());
    return items.length > 0 ? items : [text];
  }

  if (type === 'generate_seo') {
    // Split by labeled sections
    const items = text.split(/\n(?=\*\*|\d+\.)/).filter(item => item.trim());
    return items.length > 0 ? items : [text];
  }

  // For other types, return as single item
  return [text];
}

// =============================================================================
// SERVICE
// =============================================================================

class AIService {
  private usageStats: AIUsageStats = {
    totalRequests: 0,
    requestsByType: {
      generate_ideas: 0,
      improve_content: 0,
      generate_headlines: 0,
      generate_seo: 0,
      generate_social: 0,
      summarize: 0,
      expand: 0,
    },
    tokensUsed: 0,
    lastRequestAt: null,
  };

  /**
   * Generate AI content
   */
  async generate(request: AIRequest): Promise<ApiResponse<AIResponse>> {
    try {
      logger.info('api', 'AI request initiated', { type: request.type });

      // Build context string
      let contextStr = '';
      if (request.context) {
        if (request.context.contentType) {
          contextStr += `Content Type: ${request.context.contentType}\n`;
        }
        if (request.context.platform) {
          contextStr += `Platform: ${request.context.platform}\n`;
        }
        if (request.context.tone) {
          contextStr += `Tone: ${request.context.tone}\n`;
        }
        if (request.context.targetAudience) {
          contextStr += `Target Audience: ${request.context.targetAudience}\n`;
        }
      }

      let suggestions: string[];
      let tokensUsed = 0;
      let model = DEFAULT_MODEL;

      // Check if Claude API is configured
      if (isClaudeConfigured()) {
        try {
          // Build the prompt
          const promptTemplate = AI_PROMPTS[request.type];
          const prompt = promptTemplate
            .replace('{input}', request.input)
            .replace('{context}', contextStr || 'No additional context provided.');

          // Call Claude API
          const result = await callClaudeAPI(prompt);
          suggestions = parseClaudeResponse(result.text, request.type);
          tokensUsed = result.usage.input_tokens + result.usage.output_tokens;

          logger.info('api', 'Claude API call successful', {
            type: request.type,
            tokensUsed,
          });
        } catch (apiError) {
          logger.warn('api', 'Claude API call failed, falling back to mock', apiError);
          // Fall back to mock if API call fails
          suggestions = generateMockResponse(request.type, request.input);
          tokensUsed = 0;
        }
      } else {
        // Use mock responses when API key not configured
        logger.info('api', 'Using mock response (API key not configured)');
        await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 500));
        suggestions = generateMockResponse(request.type, request.input);
        tokensUsed = 0;
      }

      // Update usage stats
      this.usageStats.totalRequests++;
      this.usageStats.requestsByType[request.type]++;
      this.usageStats.tokensUsed += tokensUsed;
      this.usageStats.lastRequestAt = new Date().toISOString();

      const response: AIResponse = {
        id: `ai-${Date.now()}`,
        type: request.type,
        suggestions,
        metadata: {
          tokensUsed,
          model: isClaudeConfigured() ? model : 'mock',
        },
        createdAt: new Date().toISOString(),
      };

      logger.info('api', 'AI request completed', {
        type: request.type,
        suggestionsCount: suggestions.length,
        usingRealAPI: isClaudeConfigured(),
      });

      return { data: response, error: null };
    } catch (error) {
      logger.error('api', 'AI request failed', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Generate content ideas
   */
  async generateIdeas(topic: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'generate_ideas',
      input: topic,
      context,
    });
  }

  /**
   * Improve existing content
   */
  async improveContent(content: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'improve_content',
      input: content,
      context,
    });
  }

  /**
   * Generate headlines
   */
  async generateHeadlines(topic: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'generate_headlines',
      input: topic,
      context,
    });
  }

  /**
   * Generate SEO elements
   */
  async generateSEO(content: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'generate_seo',
      input: content,
      context,
    });
  }

  /**
   * Generate social media posts
   */
  async generateSocialPosts(content: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'generate_social',
      input: content,
      context,
    });
  }

  /**
   * Summarize content
   */
  async summarize(content: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'summarize',
      input: content,
      context,
    });
  }

  /**
   * Expand content
   */
  async expand(content: string, context?: AIRequest['context']): Promise<ApiResponse<AIResponse>> {
    return this.generate({
      type: 'expand',
      input: content,
      context,
    });
  }

  /**
   * Get usage statistics
   */
  getUsageStats(): AIUsageStats {
    return { ...this.usageStats };
  }

  /**
   * Reset usage statistics
   */
  resetUsageStats(): void {
    this.usageStats = {
      totalRequests: 0,
      requestsByType: {
        generate_ideas: 0,
        improve_content: 0,
        generate_headlines: 0,
        generate_seo: 0,
        generate_social: 0,
        summarize: 0,
        expand: 0,
      },
      tokensUsed: 0,
      lastRequestAt: null,
    };
  }

  /**
   * Get the prompt template for a feature type
   */
  getPromptTemplate(type: AIFeatureType): string {
    return AI_PROMPTS[type];
  }
}

export const aiService = new AIService();
export default aiService;
